/**
 * Global drag handle extension for TipTap.
 * Forked from: https://github.com/NiclasDev63/tiptap-extension-global-drag-handle
 * Adds onNodeChange callback to notify when the hovered node changes.
 */

import { Extension } from "@tiptap/core";
import { NodeSelection, Plugin, PluginKey, TextSelection } from "@tiptap/pm/state";
import { Fragment, Slice, Node as ProseMirrorNode } from "@tiptap/pm/model";
import * as pmView from "@tiptap/pm/view";
import type { EditorView } from "@tiptap/pm/view";

export interface NodeChangeData {
  node: ProseMirrorNode;
  pos: number;
  dom: Element;
}

export interface GlobalDragHandleOptions {
  dragHandleWidth: number;
  scrollTreshold: number;
  excludedTags: string[];
  customNodes: string[];
  dragHandleSelector?: string;
  fixedX?: number;
  onNodeChange?: (data: NodeChangeData | null) => void;
}

// Clipboard serializer
function serializeForClipboard(
  view: EditorView,
  slice: Slice
): { dom: HTMLElement; text: string } {
  // Newer Tiptap/ProseMirror
  if (view && typeof (view as unknown as { serializeForClipboard: unknown }).serializeForClipboard === "function") {
    return (view as unknown as { serializeForClipboard: (slice: Slice) => { dom: HTMLElement; text: string } }).serializeForClipboard(slice);
  }
  // Older version fallback
  if (pmView && typeof (pmView as unknown as { __serializeForClipboard: unknown }).__serializeForClipboard === "function") {
    return (pmView as unknown as { __serializeForClipboard: (view: EditorView, slice: Slice) => { dom: HTMLElement; text: string } }).__serializeForClipboard(view, slice);
  }
  throw new Error("No supported clipboard serialization method found.");
}

function absoluteRect(node: Element): { top: number; left: number; width: number } {
  const data = node.getBoundingClientRect();
  const modal = node.closest('[role="dialog"]');

  if (modal && window.getComputedStyle(modal).transform !== "none") {
    const modalRect = modal.getBoundingClientRect();
    return {
      top: data.top - modalRect.top,
      left: data.left - modalRect.left,
      width: data.width,
    };
  }
  return {
    top: data.top,
    left: data.left,
    width: data.width,
  };
}

function nodeDOMAtCoords(
  coords: { x: number; y: number },
  options: GlobalDragHandleOptions
): Element | undefined {
  const selectors = [
    "li",
    "p:not(:first-child)",
    "pre",
    "blockquote",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    ...options.customNodes.map((node) => `[data-type=${node}]`),
  ].join(", ");

  return document
    .elementsFromPoint(coords.x, coords.y)
    .find(
      (elem) =>
        elem.parentElement?.matches?.(".ProseMirror") || elem.matches(selectors)
    );
}

function nodePosAtDOM(
  node: Element,
  view: EditorView,
  options: GlobalDragHandleOptions
): number | undefined {
  const boundingRect = node.getBoundingClientRect();
  return view.posAtCoords({
    left: boundingRect.left + 50 + options.dragHandleWidth,
    top: boundingRect.top + 1,
  })?.inside;
}

function calcNodePos(pos: number, view: EditorView): number {
  const $pos = view.state.doc.resolve(pos);
  if ($pos.depth > 1) return $pos.before($pos.depth);
  return pos;
}

export function DragHandlePlugin(options: GlobalDragHandleOptions & { pluginKey: string }): Plugin {
  let listType = "";
  let currentNodePos: number | null = null;

  function handleDragStart(event: DragEvent, view: EditorView): void {
    view.focus();

    if (!event.dataTransfer) return;

    const node = nodeDOMAtCoords(
      {
        x: event.clientX + 50 + options.dragHandleWidth,
        y: event.clientY,
      },
      options
    );

    if (!(node instanceof Element)) return;

    let draggedNodePos = nodePosAtDOM(node, view, options);
    if (draggedNodePos == null || draggedNodePos < 0) return;
    draggedNodePos = calcNodePos(draggedNodePos, view);

    const { from, to } = view.state.selection;
    const diff = from - to;

    const fromSelectionPos = calcNodePos(from, view);
    let differentNodeSelected = false;

    const nodePos = view.state.doc.resolve(fromSelectionPos);

    if (nodePos.node().type.name === "doc") {
      differentNodeSelected = true;
    } else {
      const nodeSelection = NodeSelection.create(view.state.doc, nodePos.before());
      differentNodeSelected = !(
        draggedNodePos + 1 >= nodeSelection.$from.pos &&
        draggedNodePos <= nodeSelection.$to.pos
      );
    }

    let selection = view.state.selection;
    if (
      !differentNodeSelected &&
      diff !== 0 &&
      !(view.state.selection instanceof NodeSelection)
    ) {
      const endSelection = NodeSelection.create(view.state.doc, to - 1);
      selection = TextSelection.create(
        view.state.doc,
        draggedNodePos,
        endSelection.$to.pos
      );
    } else {
      selection = NodeSelection.create(view.state.doc, draggedNodePos);

      if (
        (selection as NodeSelection).node.type.isInline ||
        (selection as NodeSelection).node.type.name === "tableRow"
      ) {
        const $pos = view.state.doc.resolve(selection.from);
        selection = NodeSelection.create(view.state.doc, $pos.before());
      }
    }
    view.dispatch(view.state.tr.setSelection(selection));

    if (
      view.state.selection instanceof NodeSelection &&
      view.state.selection.node.type.name === "listItem"
    ) {
      listType = node.parentElement?.tagName || "";
    }

    const slice = view.state.selection.content();
    const { dom, text } = serializeForClipboard(view, slice);

    event.dataTransfer.clearData();
    event.dataTransfer.setData("text/html", dom.innerHTML);
    event.dataTransfer.setData("text/plain", text);
    event.dataTransfer.effectAllowed = "copyMove";

    event.dataTransfer.setDragImage(node, 0, 0);

    (view as unknown as { dragging: { slice: Slice; move: boolean } }).dragging = { slice, move: event.ctrlKey };
  }

  let dragHandleElement: HTMLElement | null = null;

  function hideDragHandle(): void {
    if (dragHandleElement) {
      dragHandleElement.classList.add("hide");
    }
    // Notify that no node is hovered
    if (options.onNodeChange) {
      options.onNodeChange(null);
    }
    currentNodePos = null;
  }

  function showDragHandle(): void {
    if (dragHandleElement) {
      dragHandleElement.classList.remove("hide");
    }
  }

  function hideHandleOnEditorOut(event: MouseEvent): void {
    if (event.target instanceof Element) {
      const relatedTarget = event.relatedTarget as Element | null;
      const isInsideEditor =
        relatedTarget?.classList.contains("tiptap") ||
        relatedTarget?.classList.contains("drag-handle");

      if (isInsideEditor) return;
    }
    hideDragHandle();
  }

  return new Plugin({
    key: new PluginKey(options.pluginKey),
    view: (view) => {
      const handleBySelector = options.dragHandleSelector
        ? document.querySelector(options.dragHandleSelector)
        : null;
      dragHandleElement = (handleBySelector as HTMLElement) ?? document.createElement("div");
      dragHandleElement.draggable = true;
      dragHandleElement.dataset.dragHandle = "";
      dragHandleElement.classList.add("drag-handle");

      function onDragHandleDragStart(e: DragEvent): void {
        handleDragStart(e, view);
      }

      dragHandleElement.addEventListener("dragstart", onDragHandleDragStart as EventListener);

      function onDragHandleDrag(e: DragEvent): void {
        hideDragHandle();
        const scrollY = window.scrollY;
        if (e.clientY < options.scrollTreshold) {
          window.scrollTo({ top: scrollY - 30, behavior: "smooth" });
        } else if (window.innerHeight - e.clientY < options.scrollTreshold) {
          window.scrollTo({ top: scrollY + 30, behavior: "smooth" });
        }
      }

      dragHandleElement.addEventListener("drag", onDragHandleDrag as EventListener);

      hideDragHandle();

      if (!handleBySelector) {
        view?.dom?.parentElement?.appendChild(dragHandleElement);
      }
      view?.dom?.parentElement?.addEventListener("mouseout", hideHandleOnEditorOut as EventListener);

      return {
        destroy: () => {
          if (!handleBySelector) {
            dragHandleElement?.remove?.();
          }
          dragHandleElement?.removeEventListener("drag", onDragHandleDrag as EventListener);
          dragHandleElement?.removeEventListener("dragstart", onDragHandleDragStart as EventListener);
          dragHandleElement = null;
          view?.dom?.parentElement?.removeEventListener("mouseout", hideHandleOnEditorOut as EventListener);
        },
      };
    },
    props: {
      handleDOMEvents: {
        mousemove: (view, event) => {
          if (!view.editable) {
            return false;
          }

          const node = nodeDOMAtCoords(
            {
              x: event.clientX + 50 + options.dragHandleWidth,
              y: event.clientY,
            },
            options
          );

          const notDragging = node?.closest(".not-draggable");
          const excludedTagList = options.excludedTags.concat(["ol", "ul"]).join(", ");

          if (
            !(node instanceof Element) ||
            node.matches(excludedTagList) ||
            notDragging
          ) {
            hideDragHandle();
            return false;
          }

          const compStyle = window.getComputedStyle(node);
          const parsedLineHeight = parseInt(compStyle.lineHeight, 10);
          const lineHeight = isNaN(parsedLineHeight)
            ? parseInt(compStyle.fontSize) * 1.2
            : parsedLineHeight;
          const paddingTop = parseInt(compStyle.paddingTop, 10);

          const rect = absoluteRect(node);

          rect.top += (lineHeight - 24) / 2;
          rect.top += paddingTop;

          if (!dragHandleElement) return false;

          // Use fixed x position if provided, otherwise calculate from element
          if (options.fixedX !== undefined) {
            dragHandleElement.style.left = `${options.fixedX}px`;
          } else {
            // Li markers - need extra offset for bullet point
            if (node.matches("ul:not([data-type=taskList]) li, ol li")) {
              rect.left -= options.dragHandleWidth;
            }
            // Headings - small offset to prevent overlap
            if (node.matches("h1, h2, h3, h4, h5, h6")) {
              rect.left -= 8;
            }
            rect.width = options.dragHandleWidth;
            dragHandleElement.style.left = `${rect.left - rect.width}px`;
          }
          dragHandleElement.style.top = `${rect.top}px`;
          showDragHandle();

          // Get node position and call onNodeChange if node changed
          let nodePos = nodePosAtDOM(node, view, options);
          if (nodePos != null && nodePos >= 0) {
            nodePos = calcNodePos(nodePos, view);

            if (nodePos !== currentNodePos && options.onNodeChange) {
              currentNodePos = nodePos;
              try {
                const $pos = view.state.doc.resolve(nodePos);
                const pmNode = $pos.nodeAfter || $pos.node();
                options.onNodeChange({
                  node: pmNode,
                  pos: nodePos,
                  dom: node,
                });
              } catch {
                options.onNodeChange(null);
              }
            }
          }

          return false;
        },
        keydown: () => {
          hideDragHandle();
          return false;
        },
        mousewheel: () => {
          hideDragHandle();
          return false;
        },
        dragstart: (view) => {
          view.dom.classList.add("dragging");
          return false;
        },
        drop: (view, event) => {
          view.dom.classList.remove("dragging");
          hideDragHandle();
          let droppedNode: ProseMirrorNode | null = null;
          const dropPos = view.posAtCoords({
            left: event.clientX,
            top: event.clientY,
          });

          if (!dropPos) return false;

          if (view.state.selection instanceof NodeSelection) {
            droppedNode = view.state.selection.node;
          }
          if (!droppedNode) return false;

          const resolvedPos = view.state.doc.resolve(dropPos.pos);
          const isDroppedInsideList = resolvedPos.parent.type.name === "listItem";

          if (
            view.state.selection instanceof NodeSelection &&
            view.state.selection.node.type.name === "listItem" &&
            !isDroppedInsideList &&
            listType === "OL"
          ) {
            const newList = view.state.schema.nodes.orderedList?.createAndFill(
              null,
              droppedNode
            );
            if (newList) {
              const slice = new Slice(Fragment.from(newList), 0, 0);
              (view as unknown as { dragging: { slice: Slice; move: boolean } }).dragging = { slice, move: event.ctrlKey };
            }
          }

          return false;
        },
        dragend: (view) => {
          view.dom.classList.remove("dragging");
          return false;
        },
      },
    },
  });
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    globalDragHandle: Record<string, never>;
  }
}

const GlobalDragHandle = Extension.create<GlobalDragHandleOptions>({
  name: "globalDragHandle",

  addOptions() {
    return {
      dragHandleWidth: 20,
      scrollTreshold: 100,
      excludedTags: [],
      customNodes: [],
      dragHandleSelector: undefined,
      fixedX: undefined,
      onNodeChange: undefined,
    };
  },

  addProseMirrorPlugins() {
    return [
      DragHandlePlugin({
        pluginKey: "globalDragHandle",
        dragHandleWidth: this.options.dragHandleWidth,
        scrollTreshold: this.options.scrollTreshold,
        dragHandleSelector: this.options.dragHandleSelector,
        excludedTags: this.options.excludedTags,
        customNodes: this.options.customNodes,
        fixedX: this.options.fixedX,
        onNodeChange: this.options.onNodeChange,
      }),
    ];
  },
});

export default GlobalDragHandle;
